import { useQuery } from '@tanstack/react-query';
import { createAdminApi } from '@awj/api-client';

import { client } from './api.ts';

/**
 * The admin's reads, as hooks.
 *
 * Query keys are arrays with a section at the head — `['dashboard']`, `['products', …]` — so a
 * mutation can invalidate a whole section without listing every variant of its key. Every read
 * the panel makes goes through here, which is what makes that possible.
 */
export const admin = () => createAdminApi(client());

export function useDashboard() {
  return useQuery({
    queryKey: ['dashboard'],
    queryFn: () => admin().dashboard(),
  });
}

/**
 * The counts the dashboard shows beside the figures the dashboard endpoint returns.
 *
 * Three separate reads rather than one, because the API has no single endpoint for them and
 * inventing a client-side aggregate that fails as a unit would mean one slow query blanking the
 * whole row. Each lands on its own.
 */
export function useProductCount() {
  return useQuery({
    queryKey: ['products', 'count'],
    // One row, for its `total`. The list itself is slice 3's problem.
    queryFn: () => admin().products({ pageSize: 1 }).then((page) => page.total),
  });
}

export function useCustomerCount() {
  return useQuery({
    queryKey: ['customers', 'count'],
    queryFn: () => admin().customers({ pageSize: 1 }).then((page) => page.total),
  });
}

export function usePendingReviewCount() {
  return useQuery({
    queryKey: ['reviews', 'pending-count'],
    queryFn: () => admin().reviews({ status: 'Pending', pageSize: 1 }).then((page) => page.total),
  });
}

export function useInquiryCounts() {
  return useQuery({
    queryKey: ['inquiries', 'counts'],
    queryFn: () => admin().inquiryCounts(),
  });
}
